import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import ContactForm from '../components/ContactForm';

const FALLBACK_TEAM = [
  {
    name: 'Christian',
    role: 'Tegning og tilbud',
    email: 'christian@gribskovsmeden.dk',
    phone: '(+45) 22 62 02 65',
    image: 'https://assets.auxodev.dk/assets/a97cf6ea-7b79-4b87-946d-9f6d75bc924c/image002.jpg'
  },
  {
    name: 'Lars Baundal',
    role: 'Smedemester',
    email: 'lars@gribskovsmeden.dk',
    phone: '(+45) 61 60 42 41',
    image: 'https://assets.auxodev.dk/assets/a97cf6ea-7b79-4b87-946d-9f6d75bc924c/lars-baundal-49.jpg'
  },
  {
    name: 'Tommy Elberg',
    role: 'Værkstedsleder',
    email: '',
    phone: '(+45) 26 13 45 23',
    image: 'https://assets.auxodev.dk/assets/a97cf6ea-7b79-4b87-946d-9f6d75bc924c/tommy-359.jpg'
  }
];

export default function ContactPage() {
  const [teamMembers, setTeamMembers] = useState(FALLBACK_TEAM);

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/v2/content/medarbejdere?sort=order&order=asc')
      .then((res) => {
        if (!res.ok) throw new Error('CMS request failed');
        return res.json();
      })
      .then((data) => {
        if (data && data.items && data.items.length > 0) {
          const formatted = data.items.map((m) => ({
            name: m.name,
            role: m.role,
            email: m.email || '',
            phone: m.phone || '',
            image: Array.isArray(m.image) && m.image.length > 0 ? m.image[0] : m.image
          }));
          setTeamMembers(formatted);
        }
      })
      .catch(() => {
        // Fallback maintained
      });
  }, []);

  return (
    <>
      <SEOHead
        title="Kontakt | Gribskovsmeden ApS"
        description="Har du spørgsmål eller kommentarer? Kontakt os i dag for at høre mere om, hvad vi kan gøre for dig."
      />

      {/* Header Banner */}
      <div className="bg-stone-950 text-white py-14 md:py-20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 text-xs text-amber-400 font-semibold uppercase tracking-wider mb-3">
              <Link to="/" className="hover:text-amber-300">Forside</Link>
              <span>/</span>
              <span>Kontakt</span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
              Kontakt
            </h1>

            <p className="mt-3 text-lg sm:text-xl text-stone-300 font-medium">
              Vores kontaktinformationer
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-stone-300">
              <a
                href="tel:+4548796816"
                className="flex items-center gap-2 hover:text-white bg-stone-900/80 border border-stone-800 px-4 py-2 rounded-lg whitespace-nowrap"
              >
                <i className="fa-solid fa-phone text-amber-500" aria-hidden="true"></i>
                <span>+45 48 79 68 16</span>
              </a>
              <a
                href="mailto:lars@gribskovsmeden.dk"
                className="flex items-center gap-2 hover:text-white bg-stone-900/80 border border-stone-800 px-4 py-2 rounded-lg whitespace-nowrap"
              >
                <i className="fa-solid fa-envelope text-amber-500" aria-hidden="true"></i>
                <span>lars@gribskovsmeden.dk</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Left Column: Contact details */}
            <div className="lg:col-span-5 space-y-8">
              <div>
                <span className="text-amber-600 font-bold uppercase tracking-wider text-xs">
                  Gribskovsmeden ApS
                </span>
                <h2 className="mt-2 text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">
                  Vores kontaktinformationer
                </h2>
                <div className="mt-4 text-stone-600 text-sm leading-relaxed space-y-3">
                  <p>
                    Da vi er et firma som har stor fokus på vores kunder og god service, så kan du altid kontakte os. Vi står parat til at hjælpe.
                  </p>
                  <p>
                    Ønsker du hjælp til din næste opgave, er du meget velkommen til at kontakte os.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-stone-50 border border-stone-200 rounded-xl p-5 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-stone-900 text-amber-400 flex items-center justify-center text-lg shrink-0">
                    <i className="fa-solid fa-location-dot" aria-hidden="true"></i>
                  </div>
                  <div>
                    <div className="font-bold text-stone-900 text-sm">Gribskovsmeden ApS</div>
                    <a
                      href="https://www.google.com/maps/place/Rundinsvej%2045,%203200%20Helsinge"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-stone-600 hover:text-amber-600 text-sm mt-0.5 inline-block"
                    >
                      Rundinsvej 45, 3200 Helsinge
                    </a>
                  </div>
                </div>

                <div className="bg-stone-50 border border-stone-200 rounded-xl p-5 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-stone-900 text-amber-400 flex items-center justify-center text-lg shrink-0">
                    <i className="fa-solid fa-phone" aria-hidden="true"></i>
                  </div>
                  <div>
                    <div className="font-bold text-stone-900 text-sm">Telefon</div>
                    <div className="mt-1 space-y-1">
                      <div>
                        <a href="tel:+4548796816" className="text-stone-700 hover:text-amber-600 text-sm font-semibold whitespace-nowrap">
                          +45 48 79 68 16
                        </a>
                      </div>
                      <div>
                        <span className="text-xs text-stone-500 mr-1.5 font-medium">Mobil:</span>
                        <a href="tel:61604241" className="text-stone-700 hover:text-amber-600 text-sm font-semibold whitespace-nowrap">
                          (+45) 61 60 42 41
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-stone-50 border border-stone-200 rounded-xl p-5 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-stone-900 text-amber-400 flex items-center justify-center text-lg shrink-0">
                    <i className="fa-solid fa-envelope" aria-hidden="true"></i>
                  </div>
                  <div>
                    <div className="font-bold text-stone-900 text-sm">E-mail</div>
                    <a href="mailto:lars@gribskovsmeden.dk" className="text-stone-700 hover:text-amber-600 text-sm font-semibold mt-0.5 inline-block whitespace-nowrap">
                      lars@gribskovsmeden.dk
                    </a>
                  </div>
                </div>

                <div className="bg-stone-100 rounded-xl p-4 text-xs text-stone-600 font-medium">
                  CVR nr.: 32660681
                </div>
              </div>
            </div>

            {/* Right Column: Form */}
            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* Team Members Section */}
      <section className="py-16 bg-stone-100 border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-amber-600 font-bold uppercase tracking-wider text-xs">
              Mød vores team
            </span>
            <h2 className="mt-2 text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">
              Vores medarbejdere
            </h2>
            <p className="mt-3 text-stone-600 text-sm">
              Find kontaktoplysninger på vores medarbejdere her.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-xl lg:max-w-none mx-auto">
            {teamMembers.map((member, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl overflow-hidden border border-stone-200/90 shadow-sm flex flex-col items-center text-center p-6 transition-all hover:shadow-md"
              >
                <div className="w-32 h-32 sm:w-36 sm:h-36 rounded-full overflow-hidden mb-5 bg-stone-900 border-4 border-stone-100 shadow-sm shrink-0">
                  {member.image ? (
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover object-top block"
                      style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-stone-400 text-3xl">
                      <i className="fa-solid fa-user" aria-hidden="true"></i>
                    </div>
                  )}
                </div>

                <h3 className="text-xl font-bold text-stone-900">{member.name}</h3>
                <p className="text-amber-600 font-semibold text-xs uppercase tracking-wider mt-1">
                  {member.role}
                </p>

                <div className="mt-6 pt-5 border-t border-stone-100 w-full space-y-2.5 text-sm">
                  {member.email && (
                    <a
                      href={`mailto:${member.email}`}
                      className="flex items-center justify-center gap-2 text-stone-600 hover:text-amber-600 transition-colors whitespace-nowrap"
                    >
                      <i className="fa-solid fa-envelope text-xs text-amber-500" aria-hidden="true"></i>
                      <span>{member.email}</span>
                    </a>
                  )}
                  {member.phone && (
                    <a
                      href={`tel:${member.phone.replace(/[^0-9+]/g, '')}`}
                      className="flex items-center justify-center gap-2 text-stone-800 font-semibold hover:text-amber-600 transition-colors whitespace-nowrap"
                    >
                      <i className="fa-solid fa-phone text-xs text-amber-500" aria-hidden="true"></i>
                      <span>{member.phone}</span>
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-14 bg-white border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold text-stone-900">Find os på kortet</h3>
              <p className="text-sm text-stone-600">Rundinsvej 45, 3200 Helsinge</p>
            </div>
            <a
              href="https://www.google.com/maps/place/Rundinsvej%2045,%203200%20Helsinge"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-stone-900 text-white hover:bg-stone-800 rounded-lg text-xs font-semibold transition-colors"
            >
              <span>Åbn i Google Maps</span>
              <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true"></i>
            </a>
          </div>

          <div className="group rounded-2xl overflow-hidden shadow-sm border border-stone-200 w-full h-[350px] sm:h-[450px] max-h-[450px] bg-stone-100 transition-all duration-300 hover:shadow-lg hover:border-amber-700/40">
            <iframe
              title="Gribskovsmeden ApS Beliggenhed"
              width="100%"
              height="100%"
              frameBorder="0"
              scrolling="no"
              marginHeight="0"
              marginWidth="0"
              src="https://maps.google.com/maps?q=Rundinsvej%2045,%203200%20Helsinge&t=m&z=14&output=embed&iwloc=near"
              className="w-full h-full max-h-[450px] border-0 filter grayscale contrast-125 transition-all duration-500 group-hover:grayscale-0 group-hover:contrast-100 group-hover:saturate-125"
              loading="lazy"
            ></iframe>
          </div>
        </div>
      </section>
    </>
  );
}
